import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Share2 } from "lucide-react";
import { useApi } from "@/lib/api";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

export function ReferCard() {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/referral").then((r) => setD(r.data)).catch(() => {});
  }, [api]);
  if (!d) return null;
  const link = `${window.location.origin}/?ref=${d.code}`;
  const text = `Book self-drive cars on Hoorecars and get ₹${d.amount} off your first trip with my code ${d.code}: ${link}`;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Hoorecars", text }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success("Invite message copied");
    }
  };
  const open = d.rewards.filter((r) => r.active && !r.used_count);
  return (
    <div className="rounded-3xl border border-black/5 bg-gradient-to-br from-orange-50 to-white p-6" data-testid="refer-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-md">
          <p className="flex items-center gap-2 font-display text-lg font-semibold"><Gift className="h-5 w-5 text-[#FF5A00]" />Refer a friend, you both get ₹{d.amount}</p>
          <p className="mt-1 text-sm text-neutral-600">Your friend gets ₹{d.amount} off their first trip. You get a ₹{d.amount} code once they complete it.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-xl border border-dashed border-[#FF5A00] bg-white px-4 py-2 font-mono text-lg font-bold tracking-wider" data-testid="refer-code">{d.code}</span>
          <button onClick={() => { navigator.clipboard.writeText(d.code); toast.success("Code copied"); }} data-testid="refer-copy" className="rounded-full border border-black/10 p-2.5 hover:border-[#FF5A00]" aria-label="Copy code"><Copy className="h-4 w-4" /></button>
          <button onClick={share} data-testid="refer-share" className="flex items-center gap-1.5 rounded-full bg-[#FF5A00] px-4 py-2.5 text-sm font-semibold text-white"><Share2 className="h-4 w-4" />Invite</button>
        </div>
      </div>
      {d.stats && (
        <div className="mt-5 grid grid-cols-3 gap-3 border-t border-black/5 pt-4" data-testid="refer-stats">
          {[["Friends invited", d.stats.invited], ["Trips completed", d.stats.completed], ["Earned", `₹${d.stats.earned}`]].map(([l, v]) => (
            <div key={l} className="rounded-2xl bg-white/80 p-3 text-center"><p className="font-display text-2xl font-bold text-hc-green">{v}</p><p className="text-[11px] uppercase tracking-wider text-neutral-500">{l}</p></div>
          ))}
          <p className="col-span-3 text-sm text-neutral-700" data-testid="refer-nudge">
            {d.stats.next_milestone ? <>Invite <b>{d.stats.to_next}</b> more friend{d.stats.to_next > 1 ? "s" : ""} who complete a trip to reach <b>{d.stats.next_milestone}</b> referrals{d.stats.rank ? <> — you're <b>#{d.stats.rank}</b> of {d.stats.members} referrers</> : ""}.</> : <>You're a Hoorecars legend — thank you for spreading the word!</>}
          </p>
        </div>
      )}
      {d.leaderboard?.length > 0 && (
        <div className="mt-4" data-testid="refer-leaderboard">
          <p className="text-xs uppercase tracking-wider text-neutral-500">Top referrers</p>
          <ol className="mt-2 divide-y divide-black/5 text-sm">
            {d.leaderboard.map((r, i) => (
              <li key={`${r.name}-${i}`} className={`flex items-center justify-between py-2 ${r.is_me ? "font-semibold text-hc-green" : ""}`} data-testid={`refer-rank-${i + 1}`}>
                <span className="flex items-center gap-3"><span className="w-6 text-neutral-400">#{i + 1}</span>{r.name}{r.is_me ? " (you)" : ""}</span>
                <span className="text-neutral-600">{r.completed} completed · ₹{r.earned}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {(d.referrals.length > 0 || open.length > 0) && (
        <div className="mt-5 grid gap-3 border-t border-black/5 pt-4 text-sm sm:grid-cols-2">
          <div data-testid="refer-friends"><p className="text-xs uppercase tracking-wider text-neutral-500">Friends invited</p>
            {d.referrals.map((r) => <p key={r.created_at}>{r.referee_name} · {r.status === "REWARDED" ? "trip completed" : "trip booked"}</p>)}
            {d.referrals.length === 0 && <p className="text-neutral-500">None yet</p>}
          </div>
          <div data-testid="refer-rewards"><p className="text-xs uppercase tracking-wider text-neutral-500">Your reward codes</p>
            {open.map((r) => <p key={r.code}><b className="font-mono">{r.code}</b> · ₹{r.value} · valid till {fmtD(r.expires_on)}</p>)}
            {open.length === 0 && <p className="text-neutral-500">None yet</p>}
          </div>
        </div>
      )}
    </div>
  );
}
